import { gql } from '@apollo/client';
import {
  DateRangeFieldsFragment,
  LogicalOperatorFragment,
  SchemaFieldsFragment,
} from '../fragments';
import { CohortFragment } from './fragments';
import { FieldFieldsFragment } from '../fields';
import { EventConditionFieldsFragment } from '../events/eventConditions';
import { LlmChatMessageFieldsFragment } from '../llm/fragments';

const CohortSampleTableSummaryFragment = gql`
  fragment CohortSampleTableSummaryFields on CohortSampleTableSummary {
    totalCount
    tableSize
  }
`;
const CohortSampleTableCaptionFragment = gql`
  fragment CohortSampleTableCaptionFields on CohortSampleTableCaption {
    caption
  }
`;
const CohortSampleTableHeaderFragment = gql`
  fragment CohortSampleTableHeaderFields on CohortSampleTableHeader {
    header
    subheader
    tooltip
  }
`;

const CohortSampleTableCellFragment = gql`
  fragment CohortSampleTableCellFields on CohortSampleTableCell {
    value
  }
`;

const CohortSampleTableColumnFragment = gql`
  fragment CohortSampleTableColumnFields on CohortSampleTableColumn {
    header {
      ...CohortSampleTableHeaderFields
    }
    columnType
    cells {
      ...CohortSampleTableCellFields
    }
  }
  ${CohortSampleTableHeaderFragment}
  ${CohortSampleTableCellFragment}
`;

const CohortSampleDataSummaryFragment = gql`
  fragment CohortSampleDataSummaryFields on CohortSampleDataSummary {
    text
    number
  }
`;

export const CohortSampleTableFragment = gql`
  fragment CohortSampleTableFields on CohortSampleTable {
    columns {
      ...CohortSampleTableColumnFields
    }
    summary {
      ...CohortSampleTableSummaryFields
    }
    caption {
      ...CohortSampleTableCaptionFields
    }
  }
  ${CohortSampleTableSummaryFragment}
  ${CohortSampleTableColumnFragment}
  ${CohortSampleTableCaptionFragment}
`;

export const CohortDataFieldsFragment = gql`
  fragment CohortDataFields on CohortSampleData {
    subjectName
    tables {
      ...CohortSampleTableFields
    }
    summary {
      ...CohortSampleDataSummaryFields
    }
    isEmpty
  }
  ${CohortSampleTableFragment}
  ${CohortSampleDataSummaryFragment}
  ${CohortSampleTableCaptionFragment}
`;

export const CohortSampleOperationFragment = gql`
  fragment CohortSampleOperationFields on CohortSampleOperation {
    key
    logicOp {
      ...LogicalOperatorFields
    }
    name
  }
  ${LogicalOperatorFragment}
`;

const CompareExpandEventFragment = gql`
  fragment CompareExpandEventFields on CompareExpandEvent {
    field {
      ...FieldFields
    }
    eventCondition {
      ...EventConditionFields
    }
  }
  ${FieldFieldsFragment}
  ${EventConditionFieldsFragment}
`;

const CompareMergeEventFragment = gql`
  fragment CompareMergeEventFields on CompareMergeEvent {
    name
    isNameAutoGenerated
    eventConditions {
      ...EventConditionFields
    }
  }
  ${EventConditionFieldsFragment}
`;

const CohortCompareFragment = gql`
  fragment CohortCompareFields on CohortCompare {
    expandEvents {
      ...CompareExpandEventFields
    }
    mergeEvents {
      ...CompareMergeEventFields
    }
  }
  ${CompareExpandEventFragment}
  ${CompareMergeEventFragment}
`;

export const CohortSampleQueryFragment = gql`
  fragment CohortSampleQueryFields on CohortSampleQuery {
    schema {
      ...SchemaFields
    }
    cohort {
      ...CohortFields
    }
    operation {
      ...CohortSampleOperationFields
    }
    compare {
      ...CohortCompareFields
    }
    dateRange {
      ...DateRangeFields
    }
    samplePercentage
    sampleAbsolute
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${SchemaFieldsFragment}
  ${CohortFragment}
  ${CohortSampleOperationFragment}
  ${CohortCompareFragment}
  ${DateRangeFieldsFragment}
  ${LlmChatMessageFieldsFragment}
`;
